import React, { useState } from "react";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "./components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Calendar } from "./components/ui/calendar";

export const PRESETS = [
  { id: "this_week",  label: "This week" },
  { id: "last_week",  label: "Last week" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "last_7",     label: "Last 7 days" },
  { id: "last_30",    label: "Last 30 days" },
  { id: "last_90",    label: "Last 90 days" },
  { id: "all_time",   label: "All time" },
];

function fmtShort(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit", timeZone: "UTC" });
}
function toIso(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function TimeRangePicker({ value, onChange, currentLabel, currentStart, currentEnd }) {
  const [open, setOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState(false);
  const [customRange, setCustomRange] = useState(undefined);

  const pick = (preset) => {
    setPickerMode(false);
    onChange({ range: preset.id });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customRange?.from || !customRange?.to) return;
    onChange({
      range: "custom",
      start: toIso(customRange.from),
      end: toIso(customRange.to),
    });
    setOpen(false);
    setPickerMode(false);
  };

  const display =
    value?.range === "custom"
      ? `${fmtShort(value.start)} → ${fmtShort(value.end)}`
      : currentLabel || PRESETS.find((p) => p.id === value?.range)?.label || "Last 30 days";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="bg-white/[0.03] border border-white/10 text-zinc-200 hover:bg-white/[0.06] hover:text-white"
          data-testid="time-range-picker-trigger"
        >
          <CalendarIcon className="w-4 h-4 mr-2 text-zinc-500" />
          {display}
          <ChevronDown className="w-3.5 h-3.5 ml-2 text-zinc-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="bg-zinc-950 border border-white/10 p-0 w-auto min-w-[260px]"
        data-testid="time-range-picker-popover"
      >
        {!pickerMode ? (
          <div className="flex flex-col py-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => pick(p)}
                className={`text-left px-4 py-2 text-sm transition-colors ${
                  value?.range === p.id
                    ? "bg-red-500/10 text-red-300"
                    : "text-zinc-300 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`time-range-preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
            <div className="border-t border-white/5 my-1" />
            <button
              onClick={() => setPickerMode(true)}
              className={`text-left px-4 py-2 text-sm transition-colors ${
                value?.range === "custom"
                  ? "bg-red-500/10 text-red-300"
                  : "text-zinc-300 hover:bg-white/5 hover:text-white"
              }`}
              data-testid="time-range-preset-custom"
            >
              Custom range…
            </button>
          </div>
        ) : (
          <div className="p-3">
            <Calendar
              mode="range"
              selected={customRange}
              onSelect={setCustomRange}
              numberOfMonths={2}
              className="rounded-md text-zinc-200"
            />
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="ghost" onClick={() => setPickerMode(false)} className="text-zinc-400 hover:text-white hover:bg-white/5">
                Back
              </Button>
              <Button
                onClick={applyCustom}
                disabled={!customRange?.from || !customRange?.to}
                className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_15px_rgba(255,0,0,0.3)] disabled:opacity-40"
                data-testid="time-range-apply-custom"
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
